import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { supabase } from '../services/supabase';
import { useToast } from '../services/ToastContext';
import '../styles/CreatePost.css';

const URL_REGEX = /https?:\/\/[^\s<>"']+/gi;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_IMAGES = 6;

function CreatePost({ onPostCreated }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { addToast } = useToast();

  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);

  const [linkPreview, setLinkPreview] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const lastFetchedUrlRef = useRef('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    const urls = content.match(URL_REGEX);
    const firstUrl = urls?.[0];

    if (!firstUrl) {
      setLinkPreview(null);
      lastFetchedUrlRef.current = '';
      return;
    }
    if (firstUrl === lastFetchedUrlRef.current) return;

    const timer = setTimeout(async () => {
      setLinkLoading(true);
      try {
        const res = await api.get(`/posts/link-preview?url=${encodeURIComponent(firstUrl)}`);
        setLinkPreview(res.data);
        lastFetchedUrlRef.current = firstUrl;
      } catch {
        setLinkPreview(null);
      } finally {
        setLinkLoading(false);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [content]);

  const handleImageSelect = (e) => {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;

    const remaining = MAX_IMAGES - imageFiles.length;
    const toAdd = selected.slice(0, remaining);
    const oversized = toAdd.filter(f => f.size > MAX_FILE_SIZE);

    if (oversized.length) {
      setError(`${oversized.length} image(s) exceed 5MB and were skipped`);
    } else {
      setError('');
    }

    const valid = toAdd.filter(f => f.size <= MAX_FILE_SIZE);
    setImageFiles(prev => [...prev, ...valid]);
    setImagePreviews(prev => [...prev, ...valid.map(f => URL.createObjectURL(f))]);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImage = async (file) => {
    const session = (await supabase.auth.getSession()).data.session;
    const userId = session?.user?.id;
    const filename = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;

    const { error: uploadError } = await supabase.storage
      .from('post-images')
      .upload(filename, file, { contentType: file.type });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('post-images')
      .getPublicUrl(filename);

    return publicUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content.trim() && imageFiles.length === 0) {
      setError('Post cannot be empty');
      return;
    }

    setError('');
    setLoading(true);

    try {
      let imageUrls = [];

      if (imageFiles.length > 0) {
        setImageUploading(true);
        imageUrls = await Promise.all(imageFiles.map(uploadImage));
        setImageUploading(false);
      }

      const response = await api.post('/posts/', {
        content: content.trim(),
        image_urls: imageUrls,
        link_preview: linkPreview || null
      });

      setContent('');
      setImageFiles([]);
      setImagePreviews([]);
      setLinkPreview(null);
      lastFetchedUrlRef.current = '';

      if (onPostCreated) onPostCreated(response.data.post);
      addToast('Post shared!');

    } catch {
      setError('Failed to create post');
      addToast('Failed to create post', 'error');
    } finally {
      setLoading(false);
      setImageUploading(false);
    }
  };

  const canPost = (content.trim() || imageFiles.length > 0) && !loading;

  return (
    <div className="create-post-card">
      <form onSubmit={handleSubmit}>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="What's on your mind?"
          rows="3"
          disabled={loading}
        />

        {imagePreviews.length > 0 && (
          <div className={`image-preview-grid preview-count-${Math.min(imagePreviews.length, 4)}`}>
            {imagePreviews.map((src, i) => (
              <div key={i} className="image-preview-item">
                <img src={src} alt={`Preview ${i + 1}`} />
                <button type="button" className="remove-image-btn" onClick={() => removeImage(i)}>×</button>
              </div>
            ))}
          </div>
        )}

        {linkLoading && <div className="link-preview-loading">Fetching preview...</div>}
        {linkPreview && !linkLoading && (
          <div className="link-preview-composer">
            {linkPreview.image && <img src={linkPreview.image} alt="" className="link-preview-img" />}
            <div className="link-preview-text">
              {linkPreview.title && <p className="link-preview-title">{linkPreview.title}</p>}
              {linkPreview.description && <p className="link-preview-desc">{linkPreview.description}</p>}
              <p className="link-preview-domain">{linkPreview.domain}</p>
            </div>
            <button type="button" className="remove-image-btn" onClick={() => { setLinkPreview(null); lastFetchedUrlRef.current = '__dismissed__'; }}>×</button>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        <div className="create-post-actions">
          <div className="create-post-left">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              style={{ display: 'none' }}
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className={`attach-btn ${imageFiles.length >= MAX_IMAGES ? 'attach-btn-disabled' : ''}`}
              title={imageFiles.length >= MAX_IMAGES ? 'Max 6 images' : 'Add photos'}
            >
              📷
            </label>
            {imageFiles.length > 0 && (
              <span className="image-count">{imageFiles.length}/{MAX_IMAGES}</span>
            )}
          </div>
          <button type="submit" disabled={!canPost} className="btn-primary">
            {imageUploading ? 'Uploading...' : loading ? 'Posting...' : 'Post'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreatePost;
